/**
 * BilanCard Component
 *
 * Displays a summary card for an accounting statement.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AccountingStatementSummary } from '@budgira/shared';
import { cn } from '../../utils/cn';

interface BilanCardProps {
  statement: AccountingStatementSummary;
  onDelete?: (id: string) => void;
  onCompare?: (id: string) => void;
}

export const BilanCard = ({ statement, onDelete, onCompare }: BilanCardProps) => {
  const { t, i18n } = useTranslation();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US');
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
    });
  };

  const isPositive = statement.netResult >= 0;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/bilans/${statement.id}`}
            className="text-lg font-semibold text-slate-900 dark:text-slate-100 hover:text-violet-600 dark:hover:text-violet-400 truncate block"
          >
            {statement.name}
          </Link>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {formatDate(statement.startDate)} - {formatDate(statement.endDate)}
          </p>
        </div>
        <div className="flex gap-2 ml-2">
          {onCompare && (
            <button
              onClick={() => onCompare(statement.id)}
              className="p-2 text-slate-400 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
              title={t('bilans.compare')}
            >
              <CompareIcon className="w-5 h-5" />
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(statement.id)}
              className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title={t('common.delete')}
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <p className="text-slate-500 dark:text-slate-400">{t('bilans.income')}</p>
          <p className="font-medium text-green-600 dark:text-green-400">
            +{formatAmount(statement.totalIncome)}
          </p>
        </div>
        <div>
          <p className="text-slate-500 dark:text-slate-400">{t('bilans.expenses')}</p>
          <p className="font-medium text-red-600 dark:text-red-400">
            -{formatAmount(statement.totalExpenses)}
          </p>
        </div>
        <div>
          <p className="text-slate-500 dark:text-slate-400">{t('bilans.netResult')}</p>
          <p
            className={cn(
              'font-medium',
              isPositive
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            )}
          >
            {isPositive ? '+' : ''}
            {formatAmount(statement.netResult)}
          </p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between">
        <span className="text-xs text-slate-400 dark:text-slate-500">
          {statement.accountCount} {statement.accountCount > 1 ? t('bilans.accounts') : t('bilans.account')}
        </span>
        <Link
          to={`/bilans/${statement.id}`}
          className="text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
        >
          {t('bilans.viewDetails')} →
        </Link>
      </div>
    </div>
  );
};

const CompareIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export default BilanCard;
